import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Theme system for the "After Hours" design language.
 *
 * Two themes are pure CSS-variable sets defined in `ds/tokens.css`, keyed on
 * `data-theme` on <html> (design system D-01):
 *   light → Daylight (warm light, the all-day app)
 *   dark  → After Hours (lamplight dark)
 *
 * The user-facing mode is one of:
 *   'auto'  → resolve by time of day (Daylight by day, After Hours after ~6pm)
 *   'day'   → force Daylight
 *   'night' → force After Hours
 *
 * Default: 'auto' — now that every screen is token-driven, the app resolves
 * Daylight by day and After Hours after ~6pm (a manual choice in localStorage
 * always wins). Flipped from the Phase-1 'night' pin once the redesign landed.
 */

export type ThemeMode = 'auto' | 'day' | 'night'
export type EffectiveTheme = 'light' | 'dark'

const STORAGE_KEY = 'marva_theme'
const DEFAULT_MODE: ThemeMode = 'auto'

/** Daylight runs 06:00–17:59; After Hours from 18:00. Shared with the no-flash boot script. */
export function resolveTheme(mode: ThemeMode, hour: number): EffectiveTheme {
  if (mode === 'day') return 'light'
  if (mode === 'night') return 'dark'
  return hour >= 6 && hour < 18 ? 'light' : 'dark'
}

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'auto' || v === 'day' || v === 'night') return v
  } catch {
    /* localStorage unavailable (SSR / privacy mode) */
  }
  return DEFAULT_MODE
}

function applyTheme(theme: EffectiveTheme): void {
  const root = document.documentElement
  // `data-theme` is canonical (design system D-01): src/ds/tokens.css keys every token on it.
  root.setAttribute('data-theme', theme)
}

interface ThemeContextValue {
  mode: ThemeMode
  theme: EffectiveTheme
  setMode: (mode: ThemeMode) => void
  /** Convenience cycle for a single toggle button: auto → day → night → auto. */
  cycleMode: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode())
  const [theme, setTheme] = useState<EffectiveTheme>(() =>
    resolveTheme(readStoredMode(), new Date().getHours())
  )

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore persistence failure */
    }
  }, [])

  const cycleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === 'auto' ? 'day' : prev === 'day' ? 'night' : 'auto'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  // Recompute + apply whenever the mode changes.
  useEffect(() => {
    const resolved = resolveTheme(mode, new Date().getHours())
    setTheme(resolved)
    applyTheme(resolved)
  }, [mode])

  // In 'auto', re-check periodically so the theme flips across the 6am/6pm boundary
  // without a reload.
  useEffect(() => {
    if (mode !== 'auto') return
    const id = window.setInterval(() => {
      const resolved = resolveTheme('auto', new Date().getHours())
      setTheme((prev) => {
        if (prev !== resolved) applyTheme(resolved)
        return resolved
      })
    }, 60_000)
    return () => window.clearInterval(id)
  }, [mode])

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, theme, setMode, cycleMode }),
    [mode, theme, setMode, cycleMode]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
